import backend from "./backend";
import constants from "../constants";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const TERMINAL_STATUSES = new Set([
  constants.DOWNLOAD_JOB_STATUS_COMPLETED,
  constants.DOWNLOAD_JOB_STATUS_FAILED,
  constants.DOWNLOAD_JOB_STATUS_EXPIRED,
]);

/**
 * Poll the recordings-download status endpoint until the job reaches a
 * terminal state (COMPLETED / FAILED / EXPIRED) or the attempt budget is
 * exhausted.
 *
 * @param {string} token  GC token (same value used by other backend calls).
 * @param {string} jobId  UUID returned by `backend.downloadRecordings`.
 * @param {object} [opts]
 * @param {number} [opts.intervalMs=DOWNLOAD_POLL_INTERVAL_MS]
 * @param {number} [opts.maxAttempts=DOWNLOAD_POLL_MAX_ATTEMPTS]
 * @param {(progress: { jobstatus: string, recordingsCount?: number, attempt: number, maxAttempts: number }) => void} [opts.onProgress]
 * @returns {Promise<object>} the terminal status payload.
 */
export const pollDownloadJob = async (token, jobId, opts = {}) => {
  const intervalMs = opts.intervalMs || constants.DOWNLOAD_POLL_INTERVAL_MS;
  const maxAttempts = opts.maxAttempts || constants.DOWNLOAD_POLL_MAX_ATTEMPTS;
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;

  // Brief tolerance for the ~1s window where DDB consistency may not have
  // caught up to the just-written job row.
  let consecutiveNotFound = 0;
  const maxNotFound = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let status;
    try {
      status = await backend.getDownloadRecordingsStatus(token, jobId);
    } catch (err) {
      if (err && err.status === 404 && consecutiveNotFound < maxNotFound) {
        consecutiveNotFound += 1;
        await sleep(intervalMs);
        continue;
      }
      throw err;
    }
    consecutiveNotFound = 0;

    if (onProgress) {
      try {
        onProgress({
          jobstatus: status && status.jobstatus,
          recordingsCount: status && status.recordingsCount,
          attempt,
          maxAttempts,
        });
      } catch (cbError) {
        console.warn("downloadJob.onProgress threw", cbError);
      }
    }

    if (status && TERMINAL_STATUSES.has(status.jobstatus)) {
      return status;
    }

    if (attempt < maxAttempts) {
      await sleep(intervalMs);
    }
  }

  const timeoutError = new Error(
    `Download job ${jobId} did not finish within the polling window (${maxAttempts} attempts).`
  );
  timeoutError.code = "DOWNLOAD_JOB_TIMEOUT";
  throw timeoutError;
};

/**
 * Fetch a presigned ZIP URL and synthesize a click on a hidden anchor so the
 * browser saves it locally.
 */
export const triggerBrowserDownload = async (presignedUrl, filename) => {
  if (!presignedUrl || presignedUrl === "#") {
    throw new Error("No download URL is available for this job.");
  }

  const response = await fetch(presignedUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to download file (HTTP ${response.status} - ${response.statusText})`
    );
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.style.display = "none";
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export default {
  pollDownloadJob,
  triggerBrowserDownload,
};
